/**
 * Merchant-app telemetry (self-serve MVP pilot instrumentation, B10).
 *
 * Fire-and-forget pings through the existing tracking ingestion
 * (/api/funnel/track — unauthenticated, store_id-partitioned S3). Event types
 * are prefixed 'app_' — the aggregation pipeline's event-processor excludes
 * that prefix from ALL storefront analytics (sessions/visitors/funnel) and
 * counts it into the visible `app_telemetry` bucket instead, which doubles as
 * the pilot funnel readout.
 *
 * Never throws, never blocks UI — losing a ping is fine; breaking the wizard
 * over telemetry is not.
 */

import { getApiBaseUrl } from '@/config/domains'
import { authService } from '@/api/auth'

const SESSION_KEY = 'tnx_app_session'

function appSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = `app_${crypto.randomUUID()}`
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return 'app_unknown'
  }
}

export type AppTelemetryEvent =
  | 'proposal_viewed'
  | 'wizard_started'
  | 'wizard_abandoned'
  | 'wizard_completed'
  | 'launched'
  | 'results_viewed'
  // Merchant stopped a running test from the results screen — a pilot signal in
  // its own right (did they trust it enough to let it finish?).
  | 'test_stopped'
  // Merchant re-ran the same matchup after stopping it — the "act again" half of
  // the self-serve loop, without going back through the wizard.
  | 'test_relaunched'
  // Optimization slots. `optimization_viewed` is the return signal for a product whose whole promise
  // is that it keeps working unattended; the control events say whether merchants trust the
  // automation or reach for the manual override.
  | 'optimization_viewed'
  | 'optimization_pinned'
  | 'optimization_unpinned'
  | 'optimization_retired'
  | 'optimization_reconfirmed'
  | 'optimization_stopped'
  // Tu tráfico. `traffic_shared` is the one that matters most: it says the merchant took a
  // finding to whoever runs their ads, which is the whole point of the screen for the (many)
  // stores where the merchant doesn't hold the controls. `campaign_tagged` measures whether
  // the optional nudge is answerable at all — "no sé" counts, and is data.
  | 'traffic_viewed'
  | 'campaign_tagged'
  | 'traffic_shared'
  // Hygiene fichas (roadmap R1 P9/P10 + U5). `hygiene_listo` is the act-rate numerator the
  // roadmap measures from R1; `hygiene_no_me_interesa` is the suppression signal the library
  // learns from. `phase0_viewed` = a merchant saw the "todavía no vemos visitas" push.
  | 'hygiene_card_viewed'
  | 'hygiene_listo'
  // R2 queue: the week's cards on Inicio / Esta semana and the three answers on plan cards.
  | 'queue_viewed'
  | 'card_listo'
  | 'card_ahora_no'
  | 'card_no_me_interesa'
  | 'hygiene_no_me_interesa'
  | 'phase0_viewed'
  // Lectura (R1 U3): the merchant opened the baseline read. `lectura` = preliminar | locked.
  | 'lectura_viewed'
  // Preferencias (roadmap v1.5): posture is the one switch a merchant declares. There is no
  // onboarding form. `postura_changed` carries from/to and what the previous source was.
  | 'preferencias_viewed'
  | 'postura_changed'
  // R2 push channel: the weekly brief's delivery switches (channel / day / hour / resubscribe).
  | 'delivery_prefs_changed'

export function trackAppEvent(name: AppTelemetryEvent, data?: Record<string, unknown>): void {
  try {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    void fetch(`${getApiBaseUrl()}/api/funnel/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        store_id: storeId,
        event_type: `app_${name}`,
        visitor_id: `app_${storeId}`,
        session_id: appSessionId(),
        timestamp: new Date().toISOString(),
        event_data: { source: 'tnx_app', ...(data || {}) },
      }),
    }).catch(() => {})
  } catch {
    /* telemetry must never break the app */
  }
}
